import { SupportChatCodeSendStatus } from '../../api/public/enums/supportChatCodeSendStatus';
import { SupportChatCodeVerifyStatus } from '../../api/public/enums/supportChatCodeVerifyStatus';
import { SupportChatAvailabilityStatus } from '../../api/public/enums/supportChatAvailabilityStatus';
import { SupportChatEscalationStatus } from '../../api/public/enums/supportChatEscalationStatus';
import { SupportChatEventMatch } from '../../api/public/enums/supportChatEventMatch';
import { SupportChatEventStatus } from '../../api/public/enums/supportChatEventStatus';
import { SupportChatInvoicingStatus } from '../../api/public/enums/supportChatInvoicingStatus';
import { SupportChatLookupStatus } from '../../api/public/enums/supportChatLookupStatus';
import { SupportChatOrderSummaryStatus } from '../../api/public/enums/supportChatOrderSummaryStatus';
import { SupportChatResendStatus } from '../../api/public/enums/supportChatResendStatus';
import type { FlowContext, FlowStep, StepOption, SupportFlow } from './types';

/**
 * Version 1 flow definitions.
 *
 * NOTE: the six main categories and their sub-steps are a WORKING DRAFT. The
 * final taxonomy is derived from the ~100 real support e-mails (Phase B) — the
 * engine and the widget do not care what the flows look like, so replacing
 * this file is the only change needed.
 *
 * Conventions
 *  - answer fields: moneyLeft, eventId, eventName, orderEmail, contactEmail,
 *    purchaseRange, description, invoiceData, ...
 *  - every escalation branch passes through `contactEmail` → `confirm` →
 *    `escalate` so the support request always carries a valid reply address.
 */

const hasEventContext = (ctx: FlowContext) => ctx.contextEventId != null;
/** The server found the customer's order → it knows the event; the widget must not ask for it again. */
const orderFound = (ctx: FlowContext) =>
	ctx.answers.lookupStatus === SupportChatLookupStatus.PaidOrderFound ||
	ctx.answers.lookupStatus === SupportChatLookupStatus.OrderFoundNotPaid ||
	ctx.answers.lookupStatus === SupportChatLookupStatus.OrderFoundPendingTransfer;
const eventKnown = (ctx: FlowContext) => ctx.answers.eventId != null || !!ctx.answers.eventName || orderFound(ctx);

/**
 * Does a free-text question concern an event? V1 has no language model, so this is a
 * word-stem check (sk/cs/en, diacritics ignored): "kedy otvárate brány", "je vstup od
 * 18 rokov", "parking at the venue" → yes; "ako si vytvorím účet" → no. Owner decision:
 * the bot decides this itself instead of asking the customer.
 */
const EVENT_QUESTION_STEMS = [
	// sk / cs
	'event', 'podujat', 'akci', 'akce', 'festival', 'koncert', 'ples', 'predstaven', 'zapas', 'vstupen', 'listok', 'listk',
	'program', 'bran', 'termin', 'datum', 'parkov', 'sedadl', 'seden', 'sektor', 'kapacit', 'vekov', 'rokov', 'miesto', 'miste',
	'kona', 'zaciat', 'zacin', 'otvar', 'oblec', 'dress', 'ubytov', 'organizator', 'poradatel', 'interpret', 'ucinkuj', 'vystup',
	// en
	'ticket', 'concert', 'show', 'gate', 'doors', 'venue', 'parking', 'seat', 'capacity', 'age', 'schedule', 'lineup', 'start',
];
/** Promo-code answers the support template covers without a human (see promoCode flow). */
const TWO_CODES_STEMS = ['dva kod', 'dve kod', 'dva zlav', 'dve zlav', '2 kod', '2 zlav', 'dva vouch', '2 vouch', 'druhy kod', 'druhy zlav', 'viac kod', 'viacero kod', 'oba kod', 'obidva', 'two code', 'second code', 'both code', 'two vouch', 'two discount'];
const BLOCKED_CODE_STEMS = ['nedokonc', 'nepresla', 'neprosla', 'neprebehla', 'zlyhal', 'selhal', 'znova', 'znovu', 'opat', 'druhykrat', 'druhy krat', 'podruhe', 'blokov', 'payment failed', 'again', 'second time', 'blocked'];
const looksLikeTwoCodes = (text: unknown) => {
	const t = normalizeName(text);
	return TWO_CODES_STEMS.some((stem) => t.includes(stem));
};
const looksLikeBlockedCode = (text: unknown) => {
	const t = normalizeName(text);
	return BLOCKED_CODE_STEMS.some((stem) => t.includes(stem));
};

/** "netýka sa žiadneho eventu", "not about an event" - the customer says there is no event; do not ask for one (QA F-01). */
const EVENT_NEGATION_STEMS = [
	'netyka sa', 'netyka ziadn', 'ziadneho eventu', 'ziadny event', 'ziadnu akci', 'bez eventu', 'nie je o event', 'nesuvisi s event',
	'nema nic s event', 'nejde o event', 'mimo eventu', 'netyka se', 'zadneho eventu', 'zadne akce', 'not about an event', 'no event',
	'not related to an event', 'unrelated to an event', 'not event related',
];
const looksLikeEventQuestion = (text: unknown) => {
	const t = normalizeName(text);
	if (EVENT_NEGATION_STEMS.some((stem) => t.includes(stem))) {
		return false;
	}
	return EVENT_QUESTION_STEMS.some((stem) => t.includes(stem));
};

/**
 * Is the answer about a NAMED event? The check is scoped to one event when the customer named it
 * or when the widget was opened on its page - and then the answer has to say which one, instead
 * of talking about "an order" while the customer has orders for several events (owner, 2026-10-15).
 * The name is public data the browser already holds; the server never sends it.
 */
const eventNamed = (ctx: FlowContext) => !!(ctx.answers.eventName || ctx.contextEventName);

/**
 * Status words only (owner, 2026-10-15): the resend offer never says how many orders or
 * tickets it covers - not a number, not "one", not "several". The event is named when the
 * browser knows it; otherwise the offer is just the offer.
 */
const resendOfferKey = (ctx: FlowContext) => (eventNamed(ctx) ? 'ost.resendOfferForEvent' : 'ost.resendOffer');

/** lower-case, diacritics stripped – for cheap client-side name checks only */
const normalizeName = (value: unknown) =>
	String(value ?? '')
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();

/**
 * Owner decision: "the name on the ticket does not matter at the entrance" is
 * true ONLY for the Silné reči shows; every other event goes to support.
 */
const isSilneReci = (ctx: FlowContext) => normalizeName(ctx.answers.eventName ?? ctx.contextEventName).includes('silne reci');

/**
 * "Organizer contact not found" → straight to the hand-over. The flow itself says what
 * the customer wants (refund / invoice / event question), so nothing more is asked than
 * the reply e-mail.
 */
const noContactSteps = (messageKey: string): FlowStep[] => [
	{ id: 'organizerUnknown', type: 'message', textKey: messageKey, situationKey: 'sit.noOrganizerContact', next: 'escEvent' },
];

/**
 * Answer the bot gives on its own.
 *
 * Owner decision 2026-10-14: such an answer never ends the conversation by itself. It asks
 * whether it helped; "no" lets the customer write a message that reaches support with the
 * whole context (event, proven e-mail, what the bot already said), so nobody is left sitting
 * on an answer that did not help them.
 *
 * @param id      step id of the answer; the question and the follow-up derive from it
 * @param texts   textKey / textKeys of the answer bubble(s)
 * @param situationKey what the escalation brief should say happened, when it comes to that
 * @param escalateTo where the "no" path continues (the flow's escalation tail)
 */
const selfServiceSteps = (
	id: string,
	texts: { textKey?: FlowStep['textKey']; textKeys?: string[] },
	situationKey: string,
	escalateTo = 'escEvent',
	/**
	 * Follow-ups offered next to "yes / no": the topics folded away in the menu are offered
	 * where the customer is actually thinking about them (owner, 2026-10-15) - a discount code
	 * right after "tickets are on sale", an invoice right after the order was found.
	 */
	extraOptions: StepOption[] = [],
): FlowStep[] => [
	{ id, type: 'message', ...texts, next: id + 'Ok' },
	{
		id: id + 'Ok',
		type: 'choice',
		field: 'selfServiceHelped',
		textKey: 'common.didItHelp',
		options: [
			{ id: 'yes', labelKey: 'common.yes', value: true, next: 'resolved' },
			...extraOptions,
			{ id: 'no', labelKey: 'common.notEnough', value: false, next: id + 'More' },
		],
	},
	{
		id: id + 'More',
		type: 'text',
		field: 'description',
		multiline: true,
		maxLength: 2000,
		askMoreIfShort: 15,
		textKey: 'common.notHelpedDescribe',
		placeholderKey: 'common.describePlaceholder',
		situationKey,
		next: escalateTo,
	},
];

/** Terminal "glad it helped" step every flow with a self-service answer needs. */
const resolvedStep: FlowStep = { id: 'resolved', type: 'end', textKey: 'common.gladToHelp', resolution: 'resolved' };

/**
 * Mailbox proof (owner decision): before the first lookup under an address the customer
 * types back a code mailed to it - logged-in users included. The lookup step answers
 * `email_not_verified` until then and comes back here; `returnTo` is that lookup step.
 */
const emailCodeSteps = (returnTo: string): FlowStep[] => [
	{
		id: 'sendCode',
		type: 'backendAction',
		action: 'sendCode',
		textKey: 'code.sending',
		// already proved this very address earlier in the conversation → straight to the lookup;
		// never right after the server said this conversation holds no such proof (a replaced
		// conversation after a 410, another tab verified a different address) - skipping then
		// sent the widget straight back to the lookup in an endless loop
		skipIf: (ctx) =>
			!!ctx.answers.orderEmail && ctx.answers.verifiedEmail === ctx.answers.orderEmail && ctx.answers.lookupStatus !== SupportChatLookupStatus.EmailNotVerified,
		next: returnTo,
		// a server-reported failure ends the flow; a network hiccup gets the usual retry prompt
		onResult: { [SupportChatCodeSendStatus.Sent]: 'enterCode', '*': 'codeSendFailed' },
	},
	{
		id: 'enterCode',
		type: 'text',
		field: 'emailCode',
		textKey: 'code.enter',
		placeholderKey: 'code.placeholder',
		maxLength: 6,
		next: 'verifyCode',
	},
	{
		id: 'verifyCode',
		type: 'backendAction',
		action: 'verifyCode',
		textKey: 'code.checking',
		onResult: { [SupportChatCodeVerifyStatus.Verified]: returnTo, [SupportChatCodeVerifyStatus.Invalid]: 'codeInvalid', [SupportChatCodeVerifyStatus.Expired]: 'codeExpired', [SupportChatCodeVerifyStatus.TooManyAttempts]: 'codeExpired', [SupportChatCodeVerifyStatus.NoCode]: 'sendCode', '*': 'codeInvalid' },
		onError: 'codeInvalid',
	},
	{ id: 'codeInvalid', type: 'message', textKey: 'code.invalid', next: 'enterCode' },
	// expired or too many guesses → a fresh code (its own rate limit stops abuse)
	{ id: 'codeExpired', type: 'message', textKey: 'code.expired', next: 'sendCode' },
	{ id: 'codeSendFailed', type: 'end', textKey: 'code.sendFailed', resolution: 'human' },
];

/**
 * Shared tail: (event, when still unknown) → contact e-mail → confirmation →
 * escalate → done. Support wants e-mail + event + problem on every request, so
 * the event is asked here whenever the flow did not need it earlier.
 */
function escalationTail(reason: string, summaryFields: string[], askEvent = true): FlowStep[] {
	const eventAsk: FlowStep[] = askEvent
		? [
				{
					id: 'escEvent',
					type: 'event',
					textKey: 'event.confirmQuestion',
					skipIf: (ctx) => !hasEventContext(ctx) || eventKnown(ctx),
					next: 'escEventName',
				},
				{
					id: 'escEventName',
					type: 'text',
					field: 'eventName',
					textKey: 'event.askNameNeutral',
					placeholderKey: 'event.namePlaceholder',
					maxLength: 120,
					skipIf: eventKnown,
					next: 'contactEmailAsk',
				},
			]
		: [];
	return [
		...eventAsk,
		{
			id: 'contactEmailAsk',
			type: 'choice',
			field: 'contactEmail',
			textKey: 'contact.useOrderEmail',
			skipIf: (ctx) => !ctx.answers.orderEmail,
			next: 'contactEmail',
			options: [
				{ id: 'same', labelKey: 'contact.yesSame', value: '$answers.orderEmail', next: 'confirm' },
				{ id: 'other', labelKey: 'contact.useOther', value: null, next: 'contactEmail' },
			],
		},
		{
			id: 'contactEmail',
			type: 'email',
			field: 'contactEmail',
			textKey: 'contact.askEmail',
			skipIf: (ctx) => !!ctx.answers.contactEmail,
			next: 'confirm',
		},
		{
			id: 'confirm',
			type: 'confirmation',
			textKey: 'escalation.confirmIntro',
			summaryFields,
			next: 'escalate',
			escalationReason: reason,
		},
		{
			id: 'escalate',
			type: 'backendAction',
			action: 'escalate',
			textKey: 'escalation.sending',
			onResult: { [SupportChatEscalationStatus.Created]: 'escalated', [SupportChatEscalationStatus.Duplicate]: 'escalated', '*': 'escalateFailed' },
			escalationReason: reason,
		},
		{
			id: 'escalateFailed',
			type: 'message',
			textKey: 'escalation.failed',
			next: 'escalateFailedEnd',
		},
		{ id: 'escalateFailedEnd', type: 'end', resolution: 'human' },
		{
			id: 'escalated',
			type: 'end',
			textKeys: ['escalation.done', 'escalation.confirmationMail'],
			resolution: 'escalated',
		},
	];
}

/** @param askKey wording of the "which event" question – "bought a ticket for" only fits flows where a purchase exists */
interface EventStepOptions {
	/**
	 * Resolve the typed name on the server and let the customer confirm a fuzzy match
	 * ("Myslíte event X (dátum)?") before anything about the event is handed out
	 * (QA Z-01: "QA Koncert" must not silently become some other concert). A second
	 * "no" hands the event over to support instead of guessing again.
	 */
	confirm?: boolean;
	/** The name may be left empty - "my question is not about an event" (QA F-01). */
	optional?: boolean;
}

const confirmMatchText = (ctx: FlowContext) => (ctx.answers.matchedEventDate ? 'event.confirmMatch' : 'event.confirmMatchNoDate');
const isFuzzyMatch = (ctx: FlowContext) => ctx.answers.eventMatch === SupportChatEventMatch.Fuzzy;

const eventSteps = (next: string | ((ctx: FlowContext) => string), askKey = 'event.askName', opts: EventStepOptions = {}): FlowStep[] => [
	{
		id: 'event',
		type: 'event',
		textKey: 'event.confirmQuestion',
		skipIf: (ctx) => !hasEventContext(ctx) || eventKnown(ctx),
		next: 'eventName',
	},
	{
		id: 'eventName',
		type: 'text',
		field: 'eventName',
		textKey: askKey,
		placeholderKey: opts.optional ? 'event.namePlaceholderOptional' : 'event.namePlaceholder',
		maxLength: 120,
		required: !opts.optional,
		skipIf: eventKnown,
		// an event known by id (page context) needs no resolving; a typed name does
		next: opts.confirm ? (ctx) => (ctx.answers.eventId == null && ctx.answers.eventName ? 'resolveEvent' : resolveTarget(next, ctx)) : next,
	},
	...(opts.confirm
		? ([
				{ id: 'resolveEvent', type: 'backendAction', action: 'resolveEvent', onResult: { [SupportChatEventStatus.Found]: 'confirmEvent', '*': 'confirmEvent' }, onError: 'confirmEvent' },
				{
					id: 'confirmEvent',
					type: 'choice',
					field: 'eventConfirmed',
					textKey: confirmMatchText,
					skipIf: (ctx) => !isFuzzyMatch(ctx),
					next,
					options: [
						{ id: 'yes', labelKey: 'event.confirmMatchYes', value: true },
						{ id: 'no', labelKey: 'event.confirmMatchNo', value: false, set: { eventId: null, eventMatch: null, matchedEventName: null }, next: 'eventNameRetry' },
					],
				},
				{
					id: 'eventNameRetry',
					type: 'text',
					field: 'eventName',
					textKey: 'event.askNameAgain',
					placeholderKey: 'event.namePlaceholder',
					maxLength: 120,
					next: 'resolveEventRetry',
				},
				{ id: 'resolveEventRetry', type: 'backendAction', action: 'resolveEvent', onResult: { [SupportChatEventStatus.Found]: 'confirmEventRetry', '*': 'confirmEventRetry' }, onError: 'confirmEventRetry' },
				{
					id: 'confirmEventRetry',
					type: 'choice',
					field: 'eventConfirmed',
					textKey: confirmMatchText,
					skipIf: (ctx) => !isFuzzyMatch(ctx),
					next,
					options: [
						{ id: 'yes', labelKey: 'event.confirmMatchYes', value: true },
						{ id: 'no', labelKey: 'event.confirmMatchNo', value: false, set: { eventId: null, eventMatch: null, matchedEventName: null }, next: 'eventUnconfirmed' },
					],
				},
				{ id: 'eventUnconfirmed', type: 'message', textKey: 'event.unconfirmed', situationKey: 'sit.eventUnconfirmed', next: 'escEvent' },
			] as FlowStep[])
		: []),
];

const resolveTarget = (next: string | ((ctx: FlowContext) => string), ctx: FlowContext) => (typeof next === 'function' ? next(ctx) : next);

/**
 * Escalation flows: verify the order by e-mail first (exact order search across
 * all events, so support gets the order id), then ask for the event unless the
 * page / an earlier answer already provided it.
 */
const verifyOrderSteps = (next: string | ((ctx: FlowContext) => string)): FlowStep[] => [
	{
		id: 'verify',
		type: 'backendAction',
		action: 'lookup',
		textKey: 'tnr.lookingUp',
		onResult: { [SupportChatLookupStatus.EmailNotVerified]: 'sendCode', '*': 'event' },
		onError: 'event',
	},
	...emailCodeSteps('verify'),
	...eventSteps(next),
];

// --------------------------------------------------------------------------
// 1. Neprišla mi vstupenka
// --------------------------------------------------------------------------
/**
 * Owner decision (2026-09-05):
 *  e-mail → order search → PAID → resend the ticket.
 *  e-mail → not found → event → similar e-mail in the attendee list → support
 *           corrects the address and resends (escalation with full context).
 *  e-mail → found but NOT paid → "did the money leave your account?"
 *           yes → send the payment confirmation to support (manual check),
 *           no  → the order did not go through, place it again.
 * The event is asked only when the search needs it (nothing found / several
 * orders under the address).
 */
const ticketNotReceived: SupportFlow = {
	id: 'ticket-not-received',
	intent: 'ticket_not_received',
	titleKey: 'flow.ticketNotReceived',
	icon: '🎟️',
	priority: 12,
	enabled: true,
	audience: 'customer',
	entryStep: 'intro',
	steps: [
		{
			id: 'intro',
			type: 'message',
			// coming from "money left, ticket missing" the customer already said what happened
			textKey: (ctx) => (ctx.answers.moneyLeft !== undefined ? 'tnr.introFromPayment' : 'tnr.intro'),
			next: 'orderEmail',
		},
		{
			id: 'orderEmail',
			type: 'email',
			field: 'orderEmail',
			textKey: 'tnr.askOrderEmail',
			// owner decision: the code goes out right after the address; the lookup runs only once it is typed back
			next: 'sendCode',
		},
		{
			id: 'lookup',
			type: 'backendAction',
			action: 'lookup',
			textKey: 'tnr.lookingUp',
			onResult: {
				[SupportChatLookupStatus.EmailNotVerified]: 'sendCode',
				[SupportChatLookupStatus.PaidOrderFound]: 'resendOffer',
				[SupportChatLookupStatus.OrderFoundNotPaid]: 'notPaidInfo',
				[SupportChatLookupStatus.OrderFoundPendingTransfer]: 'pendingTransfer',
				[SupportChatLookupStatus.EventEnded]: 'eventEnded',
				[SupportChatLookupStatus.MultipleOrdersFound]: 'multiple',
				[SupportChatLookupStatus.NotFound]: 'notFound',
				'*': 'notFound',
			},
		},
		{
			id: 'resendOffer',
			type: 'choice',
			//status words only (owner, 2026-10-15): no "one" or "several" - every paid order of the event goes out, the text says just "tickets"
			textKey: 'tnr.paidFound',
			options: [
				{ id: 'resend', labelKey: 'tnr.resendButton', next: 'resend', variant: 'primary' },
				{ id: 'support', labelKey: 'tnr.contactSupportInstead', set: { situation: 'sit.tnr.supportInstead' }, next: 'supportQuestion' },
			],
		},
		{
			//the customer turned the automatic resend down - support needs to know what they actually want
			id: 'supportQuestion',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'tnr.supportQuestion',
			placeholderKey: 'common.describePlaceholder',
			next: 'escEvent',
		},
		{
			id: 'resend',
			type: 'backendAction',
			action: 'resend',
			textKey: 'tnr.resending',
			onResult: { [SupportChatResendStatus.Sent]: 'resendDone', [SupportChatResendStatus.AlreadySent]: 'resendDone', '*': 'resendFailed' },
		},
		{ id: 'resendDone', type: 'message', textKey: 'tnr.resendDone', next: 'resendHelped' },
		{
			id: 'resendHelped',
			type: 'yesNo',
			field: 'resendHelped',
			textKey: 'tnr.helped',
			options: [
				{ id: 'yes', labelKey: 'common.yes', next: 'resolved' },
				{ id: 'no', labelKey: 'common.no', next: 'resendNotHelped' },
			],
		},
		{ id: 'resolved', type: 'end', textKey: 'tnr.resolved', resolution: 'resolved' },
		{ id: 'resendNotHelped', type: 'message', textKey: 'tnr.notHelped', situationKey: 'sit.tnr.resendNotHelped', next: 'escEvent' },
		{ id: 'resendFailed', type: 'message', textKey: 'tnr.resendFailed', situationKey: 'sit.tnr.resendFailed', next: 'escEvent' },
		// --- found, but not paid
		{ id: 'notPaidInfo', type: 'message', textKey: 'tnr.notPaidFound', next: 'notPaidMoney' },
		{
			id: 'notPaidMoney',
			type: 'yesNo',
			field: 'moneyLeft',
			textKey: 'tnr.moneyQuestion',
			allowUnknown: true,
			// already answered when the customer came from the payment flow
			skipIf: (ctx) => ctx.answers.moneyLeft !== undefined,
			next: (ctx) => (ctx.answers.moneyLeft === false ? 'notPaidRepeat' : 'notPaidProof'),
		},
		{ id: 'notPaidProof', type: 'end', textKey: 'tnr.notPaidProof', resolution: 'human' },
		{ id: 'notPaidRepeat', type: 'end', textKey: 'tnr.notPaidRepeat', resolution: 'resolved' },
		// bank transfer: nothing to do, the order confirms itself when the money arrives
		{ id: 'pendingTransfer', type: 'end', textKey: 'tnr.pendingTransfer', resolution: 'resolved' },
		// event already ended: no resend
		{ id: 'eventEnded', type: 'end', textKey: 'tnr.eventEnded', resolution: 'resolved' },
		// --- nothing / too much found → narrow down by event
		// (a similar address in the attendee list is never revealed here: the server answers
		// not_found and hands the candidate to support in the escalation mail)
		{ id: 'multiple', type: 'message', textKey: 'tnr.multipleOrders', next: 'event' },
		{ id: 'notFound', type: 'message', textKey: 'tnr.notFound', next: 'event' },
		...eventSteps((ctx) => (ctx.answers.lookupStatus === SupportChatLookupStatus.MultipleOrdersFound ? 'lookup' : 'purchaseDate')),
		{
			id: 'purchaseDate',
			type: 'dateRange',
			field: 'purchaseRange',
			textKey: 'tnr.askPurchaseDate',
			options: [
				{ id: 'today', labelKey: 'date.today', next: 'lookupFallback' },
				{ id: 'yesterday', labelKey: 'date.yesterday', next: 'lookupFallback' },
				{ id: 'last7', labelKey: 'date.last7', next: 'lookupFallback' },
				{ id: 'older', labelKey: 'date.older', next: 'lookupFallback' },
				{ id: 'unknown', labelKey: 'common.dontKnow', next: 'lookupFallback' },
			],
		},
		{
			id: 'lookupFallback',
			type: 'backendAction',
			action: 'lookupFallback',
			textKey: 'tnr.lookingUpAgain',
			onResult: {
				[SupportChatLookupStatus.EmailNotVerified]: 'sendCode',
				[SupportChatLookupStatus.PaidOrderFound]: 'resendOffer',
				[SupportChatLookupStatus.OrderFoundNotPaid]: 'notPaidInfo',
				[SupportChatLookupStatus.OrderFoundPendingTransfer]: 'pendingTransfer',
				[SupportChatLookupStatus.EventEnded]: 'eventEnded',
				[SupportChatLookupStatus.NotFound]: 'stillNotFound',
				'*': 'stillNotFound',
			},
		},
		{ id: 'stillNotFound', type: 'message', textKey: 'tnr.stillNotFound', situationKey: 'sit.tnr.notFound', next: 'escEvent' },
		...emailCodeSteps('lookup'),
		...escalationTail('ticket_not_received', ['eventName', 'orderEmail', 'description', 'contactEmail', 'purchaseRange']),
	],
};

// --------------------------------------------------------------------------
// 2. Problém s platbou
// --------------------------------------------------------------------------
const paymentProblem: SupportFlow = {
	id: 'payment-problem',
	intent: 'payment_problem',
	titleKey: 'flow.paymentProblem',
	icon: '💳',
	priority: 10,
	enabled: true,
	audience: 'customer',
	entryStep: 'kind',
	steps: [
		{
			id: 'kind',
			type: 'choice',
			field: 'paymentIssue',
			textKey: 'pay.whatHappened',
			allowHuman: true,
			options: [
				{ id: 'charged_twice', labelKey: 'pay.chargedTwice', next: 'orderEmail' },
				{ id: 'failed', labelKey: 'pay.paymentFailed', next: 'failedInfo' },
				//the server can say what it sees under the proven address - support used to type this by hand
				{ id: 'status', labelKey: 'pay.checkStatus', next: 'flow:order-status' },
				// the order search answers this one directly: transfer on its way / paid / nothing arrived
				{ id: 'transfer', labelKey: 'pay.bankTransfer', set: { moneyLeft: null }, next: 'flow:ticket-not-received' },
				{ id: 'refund_pending', labelKey: 'pay.refundPending', next: 'refundPendingInfo' },
				{ id: 'other', labelKey: 'pay.other', next: 'orderEmail' },
			],
		},
		{ id: 'failedInfo', type: 'message', textKey: 'pay.failedInfo', next: 'failedHelped' },
		// support template: refund timing + "check the original transaction, it may show as cancelled"
		{ id: 'refundPendingInfo', type: 'message', textKeys: ['pay.refundPendingInfo1', 'pay.refundPendingInfo2'], next: 'refundHelped' },
		// QA Z-03: a customer who still does not see the money needs a way forward, not only the review card
		{
			id: 'refundHelped',
			type: 'yesNo',
			field: 'refundHelped',
			textKey: 'pay.refundHelped',
			options: [
				{ id: 'yes', labelKey: 'common.yes', value: true, next: 'resolved' },
				{ id: 'no', labelKey: 'pay.refundStillMissing', value: false, set: { situation: 'sit.pay.refundMissing' }, next: 'orderEmail' },
			],
		},
		{
			id: 'failedHelped',
			type: 'yesNo',
			field: 'retryHelped',
			textKey: 'pay.failedTryAgain',
			options: [
				{ id: 'yes', labelKey: 'common.yes', next: 'resolved' },
				{ id: 'no', labelKey: 'pay.stillFailing', next: 'flow:order-status' },
			],
		},
		{ id: 'resolved', type: 'end', textKey: 'common.gladToHelp', resolution: 'resolved' },
		{ id: 'orderEmail', type: 'email', field: 'orderEmail', textKey: 'common.askOrderEmail', next: 'sendCode' },
		//owner decision 2026-09-09: no order-number question - support finds the order by the verified e-mail
		...verifyOrderSteps('description'),
		{
			id: 'description',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'pay.describe',
			placeholderKey: 'common.describePlaceholder',
			next: 'escEvent',
		},
		...escalationTail('payment_problem', ['paymentIssue', 'eventName', 'orderEmail', 'description', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 3. Potrebujem upraviť faktúru
// --------------------------------------------------------------------------
/**
 * Owner decision (Phase B): the invoice flow branches ONLY on whether the event
 * has invoicing enabled. Enabled → instructions before purchase, or collect
 * billing data for an existing order and escalate. Disabled → organizer issues
 * the invoice; the ticket itself is a valid accounting document ("cenina").
 */
const invoiceChange: SupportFlow = {
	id: 'invoice-change',
	intent: 'invoice_change',
	titleKey: 'flow.invoiceChange',
	icon: '🧾',
	priority: 30,
	shortTitleKey: 'flow.invoiceChange.short',
	secondary: true,
	enabled: true,
	audience: 'customer',
	entryStep: 'event',
	steps: [
		...eventSteps('invoicing', 'event.askNameNeutral', { confirm: true }),
		{
			//silent check (owner request): the customer only sees the outcome, not that a setting is being looked up
			id: 'invoicing',
			type: 'backendAction',
			action: 'eventInvoicing',
			onResult: { [SupportChatInvoicingStatus.Enabled]: 'haveOrder', [SupportChatInvoicingStatus.Disabled]: 'organizerContact', '*': 'eventNotFound' },
			onError: 'eventNotFound',
		},
		// QA FAK-02: an unknown event must not jump straight to the contact e-mail - say so and collect what to fix
		{ id: 'eventNotFound', type: 'message', textKey: 'inv.eventNotFound', situationKey: 'sit.inv.eventNotFound', next: 'describeFix' },
		{
			id: 'describeFix',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'inv.askWhatToFix',
			placeholderKey: 'common.describePlaceholder',
			next: (ctx) => (ctx.answers.hasOrder === true ? 'invoiceData' : 'contactEmail'),
		},
		{
			id: 'haveOrder',
			type: 'choice',
			field: 'hasOrder',
			textKey: 'inv.haveOrder',
			options: [
				{ id: 'yes', labelKey: 'inv.haveOrderYes', value: true, next: 'orderEmail' },
				{ id: 'no', labelKey: 'inv.haveOrderNo', value: false, next: 'howToBuy' },
			],
		},
		{ id: 'howToBuy', type: 'end', textKeys: ['inv.howToBuy', 'inv.howToBuyTransfer'], resolution: 'resolved' },
		{
			id: 'organizerContact',
			type: 'backendAction',
			action: 'organizerContact',
			textKey: 'ref.findingOrganizer',
			onResult: { [SupportChatEventStatus.Found]: 'invoicingOff', '*': 'invoicingOffNoContact' },
			onError: 'invoicingOffNoContact',
		},
		{ id: 'invoicingOff', type: 'end', textKeys: ['inv.disabled', 'inv.disabledContact'], resolution: 'organizer' },
		{ id: 'invoicingOffNoContact', type: 'message', textKey: 'inv.disabled', next: 'organizerUnknown' },
		...noContactSteps('inv.disabledNoContact'),
		//the address is proven with a code before any order data is taken (owner request): the invoice
		//correction must come from the person who owns the order, not from anyone who knows the e-mail
		{ id: 'orderEmail', type: 'email', field: 'orderEmail', textKey: 'common.askOrderEmail', next: 'sendCode' },
		...emailCodeSteps('describeFix'),
		{
			id: 'invoiceData',
			type: 'form',
			field: 'invoiceData',
			textKey: 'inv.askNewData',
			fields: [
				{ name: 'company', labelKey: 'inv.company', required: true, maxLength: 120 },
				{ name: 'ico', labelKey: 'inv.ico', maxLength: 20 },
				{ name: 'dic', labelKey: 'inv.dic', maxLength: 20 },
				{ name: 'icdph', labelKey: 'inv.icdph', maxLength: 20 },
				{ name: 'street', labelKey: 'inv.street', required: true, maxLength: 120 },
				{ name: 'city', labelKey: 'inv.city', required: true, maxLength: 80 },
				{ name: 'zip', labelKey: 'inv.zip', required: true, maxLength: 12 },
				{ name: 'country', labelKey: 'inv.country', required: true, maxLength: 60 },
			],
			next: 'escEvent',
		},
		...escalationTail('invoice_change', ['eventName', 'orderEmail', 'description', 'invoiceData', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 4. Potrebujem zmeniť údaje
// --------------------------------------------------------------------------
const changeData: SupportFlow = {
	id: 'change-data',
	intent: 'change_data',
	titleKey: 'flow.changeData',
	icon: '✏️',
	priority: 40,
	// owner decision 2026-09-07: not in the main menu; kept for a later comeback
	enabled: false,
	audience: 'customer',
	entryStep: 'what',
	steps: [
		{
			id: 'what',
			type: 'choice',
			field: 'changeKind',
			textKey: 'chg.what',
			allowHuman: true,
			options: [
				// name on the ticket: event first – Silné reči get the self-service answer, everyone else goes to support
				{ id: 'holder', labelKey: 'chg.holderName', next: 'event' },
				{ id: 'email', labelKey: 'chg.email', next: 'orderEmail' },
				{ id: 'date', labelKey: 'chg.eventDate', next: 'organizerNote' },
				{ id: 'other', labelKey: 'chg.other', next: 'orderEmail' },
			],
		},
		{ id: 'holderInfo', type: 'message', textKey: 'chg.holderSelfService', next: 'holderHelped' },
		{
			id: 'holderHelped',
			type: 'yesNo',
			field: 'selfServiceHelped',
			textKey: 'common.didItHelp',
			options: [
				{ id: 'yes', labelKey: 'common.yes', next: 'resolved' },
				{ id: 'no', labelKey: 'common.no', next: 'orderEmail' },
			],
		},
		{ id: 'resolved', type: 'end', textKey: 'common.gladToHelp', resolution: 'resolved' },
		{ id: 'organizerNote', type: 'message', textKey: 'chg.dateIsOrganizer', situationKey: 'sit.chg.date', next: 'orderEmail' },
		{ id: 'orderEmail', type: 'email', field: 'orderEmail', textKey: 'common.askOrderEmail', next: 'sendCode' },
		...verifyOrderSteps((ctx) => {
			// holder path arrives here from the menu (no e-mail yet): decide self-service vs. support
			if (ctx.answers.changeKind === 'holder' && !ctx.answers.orderEmail) {
				return isSilneReci(ctx) ? 'holderInfo' : 'orderEmail';
			}
			return 'description';
		}),
		{
			id: 'description',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: (ctx) =>
				ctx.answers.changeKind === 'holder' ? 'chg.describeHolder' : ctx.answers.changeKind === 'email' ? 'chg.describeEmail' : 'chg.describe',
			placeholderKey: 'common.describePlaceholder',
			next: 'escEvent',
		},
		...escalationTail('change_data', ['changeKind', 'eventName', 'orderEmail', 'description', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 4b. Otázka k eventu (program, vekové obmedzenie, parkovanie, ...)
// --------------------------------------------------------------------------
/**
 * Support cannot answer these (Inviton is only the seller) and always forwards
 * the customer to the organizer's contact address from the event - the bot
 * does exactly that, in one step.
 */
/**
 * What an event question is actually about, so the answer follows from the question (owner,
 * 2026-10-19): a price question points at the event page, a date outside the listed ones at
 * the organizer, children and age at the organizer, and so on - instead of one sentence about
 * dates and prices under every question. Same word-stem matching as the router; first hit wins,
 * so "cena pre dieťa" is a price question before it is a children question.
 */
const EVENT_TOPICS: { key: string; stems: string[] }[] = [
	{ key: 'evq.answerOnsite', stems: ['na mieste', 'kupit osobne', 'zakupit osobne', 'v pokladni', 'pri vstupe', 'na miste', 'koupit osobne', 'at the door', 'at the venue', 'on site', 'on the spot', 'box office'] },
	{ key: 'evq.answerCash', stems: ['v hotovosti', 'hotovostou', 'hotovost', 'in cash', 'pay cash', 'cash on arrival', 'cash at the'] },
	{ key: 'evq.answerHours', stems: ['hocikedy', 'kedykolvek', 'kdykoliv', 'otvaracie', 'oteviraci', 'kedy je otvoren', 'kdy je otevren', 'opening hours', 'anytime', 'any time'] },
	{ key: 'evq.answerRecording', stems: ['zaznam', 'online prenos', 'recording', 'recordings', 'livestream', 'stream'] },
	{ key: 'evq.answerDates', stems: ['rezervaciu na', 'rezervovat na', 'rezervaci na', 'termin na', 'iny termin', 'jiny termin', 'dalsi termin', 'book for', 'reserve for', 'reservation for', 'booking for', 'other date', 'another date',
		'na sobotu', 'na nedelu', 'na piatok', 'na stvrtok', 'na stredu', 'na utorok', 'na pondelok', 'na nedeli', 'na patek', 'na ctvrtek', 'for saturday', 'for sunday', 'for friday', 'for sat ', 'for sun ', 'for fri '] },
	{ key: 'evq.answerPrice', stems: ['cena', 'cenu', 'ceny', 'cennik', 'kolko stoji', 'kolko stoja', 'kolik stoji', 'cena pro', 'zlava pre', 'v cene', 'how much', 'price', 'prices', 'included in the price'] },
	{ key: 'evq.answerAge', stems: ['dieta', 'dietat', 'detsk', 'deti', 'rocne', 'rocny', 'rocna', 'rokov', 'vekov', 'dite', 'detem', 'rocni', 'children', 'child', 'kids', 'toddler', 'years old', 'how old', 'age limit'] },
	// who the event is for, what one needs or should bring, how it goes (owner, 2026-10-21: "is the brunch open to people who ...")
	{
		key: 'evq.answerWho',
		stems: [
			'jednotlivec', 'nie som firma', 'sukromna osoba', 'fyzicka osoba', 'ako sukromna', 'as an individual', 'not a company', 'private person', 'private individual',
			'pre koho', 'je to pre', 'je to aj pre', 'mozem prist', 'mozem sa zucastnit', 'moze prist', 'moze sa zucastnit', 'vhodne pre', 'zaciatocn', 'musim mat', 'treba mat', 'potrebujem mat', 'co si mam priniest', 'co si priniest', 'co si vziat', 'ako to prebieha', 'ako prebieha', 'prvykrat', 'prvy raz', 'bez skusenost', 'nemam vlastn', 'nemam este', 'nemam firmu',
			'pro koho', 'je to pro', 'mohu prijit', 'muzu prijit', 'muze prijit', 'vhodne pro', 'zacatecn', 'musim mit', 'potrebuji mit', 'co si vzit', 'co si prinest', 'jak to probiha', 'poprve', 'bez zkusenost', 'nemam vlastni',
			'open to', 'who can', 'can anyone', 'can i come', 'can i attend', 'can i join', 'suitable for', 'beginner', 'beginners', 'do i need', 'do i have to', 'have to have', 'need to have', 'need to bring', 'what to wear', 'is it for', 'first time', 'newcomer', 'what is it like', 'whats it like', 'what its like', 'own business', 'without experience', 'no experience',
		],
	},
];

/** Resource key of the organizer answer that fits the question; the generic one when nothing specific was asked. */
export const eventAnswerKey = (question: unknown): string => {
	const t = normalizeName(question);
	for (const topic of EVENT_TOPICS) {
		if (topic.stems.some((stem) => hasStem(t, stem))) {
			return topic.key;
		}
	}
	return 'evq.organizerAnswer';
};

const eventQuestion: SupportFlow = {
	id: 'event-question',
	intent: 'event_question',
	titleKey: 'flow.eventQuestion',
	icon: '📅',
	priority: 20,
	// owner decision 2026-09-07: in the menu as the 8th bubble
	enabled: true,
	audience: 'customer',
	entryStep: 'intro',
	steps: [
		{ id: 'intro', type: 'message', textKey: 'evq.intro', next: 'event' },
		...eventSteps('organizerContact', 'event.askNameNeutral', { confirm: true }),
		{
			id: 'organizerContact',
			type: 'backendAction',
			action: 'organizerContact',
			textKey: 'ref.findingOrganizer',
			onResult: { [SupportChatEventStatus.Found]: 'organizerFound', '*': 'organizerUnknown' },
			onError: 'organizerUnknown',
		},
		//Owner 2026-10-19: a question about the event itself is answered on the spot - the organizer's
		//contact plus the event page for dates and prices - and only a "no" goes to support. The
		//question typed in "Iná otázka" travels along, so it is not asked for a second time.
		{ id: 'organizerFound', type: 'message', textKey: (ctx) => eventAnswerKey(ctx.answers.description), next: 'organizerFoundOk' },
		{
			id: 'organizerFoundOk',
			type: 'choice',
			field: 'selfServiceHelped',
			textKey: 'common.didItHelp',
			options: [
				{ id: 'yes', labelKey: 'common.yes', value: true, next: 'resolved' },
				{ id: 'no', labelKey: 'common.notEnough', value: false, set: { situation: 'sit.evq.notEnough' }, next: 'organizerFoundMore' },
			],
		},
		{
			id: 'organizerFoundMore',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'common.notHelpedDescribe',
			placeholderKey: 'common.describePlaceholder',
			skipIf: (ctx) => !!ctx.answers.description,
			next: 'escEvent',
		},
		resolvedStep,
		//no contact stored on the event: the bot stops and hands the question to support instead of guessing
		...noContactSteps('evq.organizerUnknown'),
		...escalationTail('event_question', ['eventName', 'description', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 4c. Zľavový kód alebo voucher
// --------------------------------------------------------------------------
/**
 * Codes and vouchers are configured by the organizer; the bot collects what
 * was entered and what happened, the event and a contact, and hands it over.
 */
const promoCode: SupportFlow = {
	id: 'promo-code',
	intent: 'promo_code',
	titleKey: 'flow.promoCode',
	icon: '🏷️',
	priority: 47,
	shortTitleKey: 'flow.promoCode.short',
	secondary: true,
	enabled: true,
	audience: 'customer',
	entryStep: 'intro',
	steps: [
		// owner decision: event first, then the code and what happened
		{ id: 'intro', type: 'message', textKey: 'promo.intro', next: 'event' },
		...eventSteps('describe', 'event.askNamePromo'),
		{
			id: 'describe',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'promo.describe',
			placeholderKey: 'common.describePlaceholder',
			// the two cases support answers from a template are answered here; everything else goes to support
			next: (ctx) => (looksLikeTwoCodes(ctx.answers.description) ? 'twoCodes' : looksLikeBlockedCode(ctx.answers.description) ? 'blockedInfo' : 'escEvent'),
		},
		{ id: 'twoCodes', type: 'end', textKeys: ['promo.twoCodes1', 'promo.twoCodes2'], resolution: 'resolved' },
		{ id: 'blockedInfo', type: 'message', textKey: 'promo.blocked', next: 'blockedHelped' },
		{
			id: 'blockedHelped',
			type: 'yesNo',
			field: 'blockedHelped',
			textKey: 'common.didItHelp',
			options: [
				{ id: 'yes', labelKey: 'common.yes', next: 'resolved' },
				{ id: 'no', labelKey: 'common.no', next: 'stillNotWorking' },
			],
		},
		{ id: 'resolved', type: 'end', textKey: 'common.gladToHelp', resolution: 'resolved' },
		{ id: 'stillNotWorking', type: 'message', textKey: 'promo.stillNotWorking', situationKey: 'sit.promo.blocked', next: 'escEvent' },
		...escalationTail('promo_code', ['eventName', 'description', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 5. Storno / vrátenie peňazí
// --------------------------------------------------------------------------
/**
 * Owner decision (Phase B): Inviton never decides refunds. The bot explains
 * that the organizer of the event decides and then writes to Inviton, shows the
 * organizer's contact e-mail from the event, and falls back to the support
 * address when no contact is stored. No request is collected.
 */
const refund: SupportFlow = {
	id: 'refund',
	intent: 'refund',
	titleKey: 'flow.refund',
	icon: '↩️',
	priority: 50,
	shortTitleKey: 'flow.refund.short',
	secondary: true,
	enabled: true,
	audience: 'customer',
	entryStep: 'intro',
	steps: [
		{ id: 'intro', type: 'message', textKey: 'ref.intro', next: 'event' },
		...eventSteps('organizerContact', 'event.askName', { confirm: true }),
		{
			id: 'organizerContact',
			type: 'backendAction',
			action: 'organizerContact',
			textKey: 'ref.findingOrganizer',
			onResult: { [SupportChatEventStatus.Found]: 'organizerFound', '*': 'organizerUnknown' },
			onError: 'organizerUnknown',
		},
		{ id: 'organizerFound', type: 'end', textKey: 'ref.organizerEmail', resolution: 'organizer' },
		// no contact on the event → support finds it; the bot collects what the customer needs
		...noContactSteps('ref.organizerUnknown'),
		...escalationTail('refund', ['eventName', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 6. Som organizátor
// --------------------------------------------------------------------------
/**
 * Owner decision (Phase B): organizers get direct human contact — call the
 * support phone (InstanceConfig.Company.PhoneNumber) or write to the support
 * e-mail. No form, no escalation through the bot.
 */
const organizer: SupportFlow = {
	id: 'organizer',
	intent: 'organizer',
	titleKey: 'flow.organizer',
	icon: '👤',
	priority: 30,
	enabled: true,
	audience: 'organizer',
	entryStep: 'topic',
	steps: [
		// phone + e-mail first (owner decision), then "or tell me right here" – the request goes to the Inviton team
		{
			id: 'topic',
			type: 'choice',
			field: 'organizerTopic',
			textKeys: ['org.contactPhoneAndEmail', 'org.orWriteHere'],
			options: [
				{ id: 'quote', labelKey: 'org.topicQuote', next: 'describe' },
				{ id: 'problem', labelKey: 'org.topicProblem', next: 'orgEvent' },
				{ id: 'setup', labelKey: 'org.topicSetup', next: 'orgEvent' },
				{ id: 'services', labelKey: 'org.topicServices', next: 'describe' },
				{ id: 'other', labelKey: 'org.topicOther', next: 'describe' },
			],
		},
		{
			id: 'orgEvent',
			type: 'text',
			field: 'eventName',
			textKey: 'event.askNameNeutral',
			placeholderKey: 'event.namePlaceholder',
			maxLength: 120,
			skipIf: (ctx) => !!ctx.answers.eventName || !!ctx.contextEventName,
			next: 'describe',
		},
		{
			id: 'describe',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 3000,
			askMoreIfShort: 15,
			textKey: 'org.describe',
			placeholderKey: 'common.describePlaceholder',
			next: 'contactEmailAsk',
		},
		...escalationTail('organizer', ['organizerTopic', 'eventName', 'description', 'contactEmail'], false),
	],
};

// --------------------------------------------------------------------------
// 8. Chcem kúpiť vstupenku / je ešte voľné?  (analysis 2026-10-14: the biggest topic)
// --------------------------------------------------------------------------
/**
 * 29 of 113 requests in the first five weeks were "can I still buy a ticket / is this date
 * free", and there was no branch for it at all - every single one went to support, although
 * the answer is in the data. The server reads the sale window and the remaining capacity with
 * the same function the shop uses (SupportChatAvailabilityProvider).
 *
 * Owner decision 2026-10-14: for events where the customer picks a seat on a map the bot never
 * says "sold out" - it only says the sale is open and links to the map.
 */
const availability: SupportFlow = {
	id: 'availability',
	intent: 'availability',
	titleKey: 'flow.availability',
	icon: '🎫',
	priority: 5,
	enabled: true,
	audience: 'customer',
	entryStep: 'intro',
	steps: [
		{ id: 'intro', type: 'message', textKey: 'avl.intro', next: 'event' },
		...eventSteps('check', 'event.askNameNeutral', { confirm: true }),
		{
			id: 'check',
			type: 'backendAction',
			action: 'availability',
			textKey: 'avl.checking',
			onResult: {
				[SupportChatAvailabilityStatus.OnSale]: 'onSale',
				[SupportChatAvailabilityStatus.SoldOut]: 'soldOut',
				[SupportChatAvailabilityStatus.SaleEnded]: 'saleEnded',
				[SupportChatAvailabilityStatus.SaleNotStarted]: 'saleNotStarted',
				[SupportChatAvailabilityStatus.NoOnlineSale]: 'noOnlineSale',
				[SupportChatAvailabilityStatus.EventEnded]: 'eventEnded',
				'*': 'notFound',
			},
			onError: 'notFound',
		},
		//the seating variant leaves the "how many are left" question to the map
		...selfServiceSteps(
			'onSale',
			{ textKey: (ctx) => (ctx.answers.seatSelection ? 'avl.onSaleSeating' : ctx.answers.nextDate ? 'avl.onSaleWithDate' : 'avl.onSale') },
			'sit.avl.onSale',
			'escEvent',
			//somebody who is about to buy is exactly who asks about a discount code
			[{ id: 'promo', labelKey: 'flow.promoCode', next: 'flow:promo-code' }],
		),
		...selfServiceSteps('soldOut', { textKey: (ctx) => (ctx.answers.otherDate ? 'avl.soldOutOtherDate' : 'avl.soldOut') }, 'sit.avl.soldOut'),
		...selfServiceSteps('saleEnded', { textKey: 'avl.saleEnded' }, 'sit.avl.saleEnded'),
		...selfServiceSteps('saleNotStarted', { textKey: 'avl.saleNotStarted' }, 'sit.avl.saleNotStarted'),
		...selfServiceSteps('noOnlineSale', { textKey: 'avl.noOnlineSale' }, 'sit.avl.noOnlineSale'),
		...selfServiceSteps('eventEnded', { textKey: 'avl.eventEnded' }, 'sit.avl.eventEnded'),
		//the event could not be resolved: say so and let support take it with the typed name
		{ id: 'notFound', type: 'message', textKey: 'avl.notFound', situationKey: 'sit.avl.notFound', next: 'notFoundDescribe' },
		{
			id: 'notFoundDescribe',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'avl.notFoundDescribe',
			placeholderKey: 'common.describePlaceholder',
			next: 'escEvent',
		},
		resolvedStep,
		...escalationTail('availability', ['eventName', 'description', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 9. Skontrolovať objednávku a platbu  (hidden: reached from the payment branch)
// --------------------------------------------------------------------------
/**
 * "Neviem, či mi prešla platba" - the answer support typed by hand in 11 of the 81 replies
 * ("we can see 4 attempts, none of them successful"). After the address is proven with a code
 * the server returns the same counts and the bot says them out loud; when a paid order is
 * found it offers the resend straight away.
 *
 * Hidden from the main menu on purpose: the menu already has eight topics, and everyone who
 * asks this arrives either through "Problém s platbou" or through free text.
 */
const orderStatus: SupportFlow = {
	id: 'order-status',
	intent: 'order_status',
	titleKey: 'flow.orderStatus',
	icon: '🔎',
	priority: 15,
	enabled: true,
	hidden: true,
	audience: 'customer',
	entryStep: 'intro',
	steps: [
		//Opened on an event page: confirm which event this is about before anything else. A customer
		//with orders for several events must not be told about "an order" without knowing which one
		//(owner, 2026-10-15). Elsewhere we do not ask - the server asks only if it finds several.
		{ id: 'intro', type: 'message', textKey: 'ost.intro', next: (ctx) => (hasEventContext(ctx) ? 'event' : 'orderEmail') },
		{ id: 'orderEmail', type: 'email', field: 'orderEmail', textKey: 'common.askOrderEmail', next: 'sendCode' },
		{
			id: 'check',
			type: 'backendAction',
			action: 'orderStatus',
			textKey: 'ost.checking',
			onResult: {
				[SupportChatOrderSummaryStatus.EmailNotVerified]: 'sendCode',
				[SupportChatOrderSummaryStatus.PaidOnly]: 'paid',
				[SupportChatOrderSummaryStatus.PaidAndFailedAttempts]: 'paidAndFailed',
				[SupportChatOrderSummaryStatus.FailedAttemptsOnly]: 'failedOnly',
				[SupportChatOrderSummaryStatus.PendingTransfer]: 'pendingTransfer',
				[SupportChatOrderSummaryStatus.NothingFound]: 'nothingFound',
				[SupportChatOrderSummaryStatus.NeedsManualCheck]: 'manualCheck',
				[SupportChatOrderSummaryStatus.PaidSeveralEvents]: 'severalEvents',
				'*': 'nothingFound',
			},
		},
		...emailCodeSteps('check'),
		// Paid orders for more than one event: ask which one first, resend nothing yet (owner
		// decision, the same rule as MultipleOrdersFound in the ticket flow). Asked once - a name
		// the server cannot resolve goes to support instead of round the loop again.
		{
			id: 'severalEvents',
			type: 'message',
			textKey: 'ost.severalEvents',
			next: (ctx) => (ctx.answers.eventName || ctx.answers.eventId != null ? 'severalEventsUnresolved' : 'event'),
		},
		//before the address is proven the event question leads to the e-mail; afterwards straight to the check
		...eventSteps((ctx) => (ctx.answers.verifiedEmail ? 'check' : 'orderEmail'), 'event.askNameNeutral', { confirm: true }),
		{ id: 'severalEventsUnresolved', type: 'message', textKey: 'ost.severalEventsUnresolved', situationKey: 'sit.ost.severalEvents', next: 'paidIssueDescribe' },
		// PartiallyAuthorized: most likely paid, but only a human looking at the gateway can tell
		// (owner, 2026-10-15). The bot claims nothing either way and hands it over.
		{ id: 'manualCheck', type: 'message', textKey: 'ost.manualCheck', situationKey: 'sit.ost.manualCheck', next: 'contactEmailAsk' },
		//paid: say what is there and offer the resend the token already covers
		//The server asks for the event only when the paid orders span several (PaidSeveralEvents).
		//With a single event the bot does not ask - it knows, and asking reads as dumb (owner,
		//2026-10-15); the wording below says instead that no other event is involved.
		{ id: 'paid', type: 'message', textKey: (ctx) => (eventNamed(ctx) ? 'ost.paidForEvent' : 'ost.paid'), next: 'paidOffer' },
		{
			id: 'paidOffer',
			type: 'choice',
			//the offer names what it covers: one order or all of them, and which event when we know it
			textKey: (ctx) => resendOfferKey(ctx),
			options: [
				{ id: 'resend', labelKey: 'ost.resendButton', next: 'resend', variant: 'primary' },
				//the order is in front of us: this is where an invoice or a cancellation is asked for
				{ id: 'invoice', labelKey: 'flow.invoiceChange', next: 'flow:invoice-change' },
				{ id: 'refund', labelKey: 'flow.refund', next: 'flow:refund' },
				// a paid order and still something wrong (charged twice, wrong amount) must reach support
				{ id: 'issue', labelKey: 'ost.somethingWrong', next: 'paidIssueDescribe' },
				{ id: 'no', labelKey: 'ost.allGood', next: 'resolved' },
			],
		},
		{
			id: 'paidIssueDescribe',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'ost.paidIssueDescribe',
			placeholderKey: 'common.describePlaceholder',
			situationKey: 'sit.ost.paidIssue',
			next: 'contactEmailAsk',
		},
		{ id: 'paidAndFailed', type: 'message', textKey: (ctx) => (eventNamed(ctx) ? 'ost.paidAndFailedForEvent' : 'ost.paidAndFailed'), next: 'paidAndFailedOffer' },
		{
			id: 'paidAndFailedOffer',
			type: 'choice',
			textKey: (ctx) => resendOfferKey(ctx),
			options: [
				{ id: 'resend', labelKey: 'ost.resendButton', next: 'resend', variant: 'primary' },
				{ id: 'money', labelKey: 'ost.moneyLeftAnyway', next: 'moneyLeftDescribe' },
				{ id: 'no', labelKey: 'ost.allGood', next: 'resolved' },
			],
		},
		{
			id: 'failedOnly',
			type: 'choice',
			textKeys: ['ost.failedOnly', 'ost.failedAdvice'],
			options: [
				{ id: 'tryAgain', labelKey: 'ost.willTryAgain', next: 'resolved' },
				{ id: 'money', labelKey: 'ost.moneyLeftAnyway', next: 'moneyLeftDescribe' },
			],
		},
		{
			id: 'moneyLeftDescribe',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'ost.moneyLeftDescribe',
			placeholderKey: 'common.describePlaceholder',
			situationKey: 'sit.ost.moneyLeft',
			next: 'contactEmailAsk',
		},
		...selfServiceSteps('pendingTransfer', { textKey: 'ost.pendingTransfer' }, 'sit.ost.pendingTransfer', 'contactEmailAsk'),
		{
			id: 'nothingFound',
			type: 'choice',
			textKey: 'ost.nothingFound',
			options: [
				{ id: 'other', labelKey: 'ost.tryOtherEmail', set: { verifiedEmail: null }, next: 'orderEmail' },
				{ id: 'support', labelKey: 'common.notEnough', next: 'nothingFoundDescribe' },
			],
		},
		{
			id: 'nothingFoundDescribe',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'common.notHelpedDescribe',
			placeholderKey: 'common.describePlaceholder',
			situationKey: 'sit.ost.nothingFound',
			next: 'contactEmailAsk',
		},
		{
			id: 'resend',
			type: 'backendAction',
			action: 'resend',
			textKey: 'tnr.resending',
			onResult: { [SupportChatResendStatus.Sent]: 'resendDone', [SupportChatResendStatus.AlreadySent]: 'resendDone', '*': 'resendFailed' },
		},
		...selfServiceSteps('resendDone', { textKey: 'tnr.resendDone' }, 'sit.tnr.resendNotHelped', 'contactEmailAsk'),
		{ id: 'resendFailed', type: 'message', textKey: 'tnr.resendFailed', situationKey: 'sit.tnr.resendFailed', next: 'contactEmailAsk' },
		resolvedStep,
		...escalationTail('order_status', ['eventName', 'orderEmail', 'description', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 7. Iné / vlastná otázka — always last
// --------------------------------------------------------------------------
/**
 * Where free text goes.
 *
 * 55 % of the requests of the first five weeks arrived through "Iné / vlastná otázka" and
 * every one of them went to support unread (analysis 2026-10-14). There is no language model
 * here: this is the same cheap word-stem match the rest of the file uses, and it only ever
 * sends the customer to a branch that CAN answer them. Anything it is not sure about keeps
 * the old behaviour and ends up with support, so a miss costs nothing.
 *
 * Order matters: the first route whose stems appear in the text wins, so narrow cases
 * ("kde zadám zľavový kód") come before the wide ones ("zľavový kód").
 */
const FREE_TEXT_ROUTES: { target: string; stems: string[]; unless?: string[] }[] = [
	//--- tickets and orders
	{
		target: 'flow:ticket-not-received',
		stems: ['neprisla vstupenka', 'neprisla mi vstupenka', 'neprisli vstupenky', 'neprisel listok', 'nedorucen', 'nemam vstupenku', 'nedostal som vstupenku', 'nedostala som vstupenku', 'neprisla mi faktura ani vstupenka', 'zly email', 'zlu emailovu', 'nespravny email', 'wrong email', 'not received', 'didnt receive', 'did not receive', 'neprisla mi na mail',
			// "where is my ticket" (week of 2026-10-23)
			'kde mam vstupenku', 'kde najdem vstupenku', 'kde mam stiahnutu', 'kde si stiahnem vstupenku', 'kde je moja vstupenka', 'where is my ticket', 'find my ticket', 'where can i find my ticket'],
	},
	{
		// charged twice is a gateway check with a refund, not a status read-out: the payment branch
		// asks the one question and hands it over with the right brief and draft
		target: 'flow:payment-problem',
		stems: ['strhnute dvakrat', 'strhnuta dvakrat', 'strhnuty dvakrat', 'strhlo dvakrat', 'strhli dvakrat', 'zaplatene dvakrat', 'platba dvakrat', 'zaplatil som dvakrat', 'zaplatila som dvakrat', 'dvojita platba', 'charged twice', 'double charge', 'paid twice'],
	},
	{
		target: 'flow:order-status',
		stems: [
			'presla platba', 'nepresla platba', 'platba nepresla', 'platba neprosla', 'presla mi platba', 'neviem ci mi presla', 'stav platby', 'stav objednavky', 'platba zlyhala', 'platba neprebehla', 'zlyhala mi platba', 'odisli mi peniaze', 'strhlo mi', 'payment failed', 'payment did not go through', 'payment didnt go through', 'did my payment',
			// a transfer already sent is a status question - the how-to-pay answer below would miss it
			'zaplatil som prevodom', 'zaplatila som prevodom', 'platil som prevodom', 'platila som prevodom', 'poslal som prevod', 'poslala som prevod', 'uhradil som prevodom', 'uhradila som prevodom', 'paid by bank transfer', 'sent a bank transfer',
		],
	},
	//--- one-sentence answers support repeated over and over
	{ target: 'kbPromoWhere', stems: ['kde zadam kod', 'kde zadam zlavovy', 'kde sa zadava kod', 'kde sa zadava zlav', 'kde mam vlozit', 'kde vlozim kod', 'where do i put', 'where to enter the code', 'how to use the discount'] },
	// paying THROUGH a wallet; "do peňaženky" (storing the ticket) is kbNoPrint below
	{ target: 'kbWalletPayment', stems: ['google pay', 'gpay', 'apple pay', 'applepay', 'platba cez penazenk', 'platit cez penazenk', 'zaplatit cez penazenk', 'platba penazenk'] },
	{ target: 'kbChangePayment', stems: ['zmenit sposob platby', 'zmena sposobu platby', 'change the payment method', 'change payment method'] },
	{ target: 'kbBankTransfer', stems: ['zaplatit bankovym prevodom', 'platit bankovym prevodom', 'zaplatit prevodom', 'platit prevodom', 'platba prevodom', 'moznost prevodu', 'bankovy prevod', 'pay by bank transfer', 'ako zaplatit', 'ako mozem zaplatit', 'platobne metody', 'payment methods', 'platba na fakturu', 'zaplatit na fakturu', 'platit na fakturu'] },
	{ target: 'kbBlueSeats', stems: ['modre', 'modrou farbou', 'namodro', 'blokovane miesta', 'rezervovane miesta', 'blue seats'] },
	{ target: 'kbNoRegistration', stems: ['musim sa registrovat', 'treba sa registrovat', 'treba registraci', 'bez registracie', 'musim mat ucet', 'need to register', 'need an account'] },
	{ target: 'kbDicOptional', stems: ['nemam dic', 'nemame dic', 'dic nie je', 'dic je povinne', 'je dic povinne', 'chce dic', 'without a vat'] },
	{ target: 'kbNoPrint', stems: ['musim vytlacit', 'treba vytlacit', 'musim tlacit', 'do i have to print', 'need to print', 'do penazenky', 'apple wallet', 'google wallet'] },
	{ target: 'kbGiftVoucher', stems: ['darkovy poukaz', 'darcekovy poukaz', 'darkovy certifikat', 'gift voucher', 'gift card'] },
	// how the ticket is used (week of 2026-10-23)
	{ target: 'kbUseTicket', stems: ['ako uplatnit', 'uplatnit vstupenku', 'ako pouzit vstupenku', 'ako funguje vstupenka', 'ako sa preukaz', 'ako sa preukazem', 'how to use the ticket', 'how do i use the ticket', 'redeem the ticket', 'redeem my ticket'] },
	{ target: 'kbAttendeeData', stems: ['je tam prihlaseny', 'je tam zapisany', 'zoznam ucastnikov', 'kto este pride', 'kto vsetko pride', 'kto je prihlaseny', 'kto sa prihlasil', 'who is attending', 'attendee list'] },
	//--- branches that need more than one sentence
	// organizers writing into the customer widget (week of 2026-10-23: integration, fees, payouts, a new event)
	{
		target: 'flow:organizer',
		stems: [
			'organizujeme', 'chceme organizovat', 'chceli by sme organizovat', 'cez vas organizovat', 'vas portal', 'vasu platformu', 'vasej platformy', 'nase podujatie', 'nasu akciu', 'predaj vstupeniek na nase', 'wordpress', 'integrovat', 'integracia', 'implementovat', 'na vlastnu stranku', 'vlastny web',
			'poplatok za sluzbu', 'poplatky za sluzbu', 'vase poplatky', 'aky je poplatok', 'ake su poplatky', 'kolko si uctujete', 'provizia', 'provizie', 'nevyplatene', 'vyplatenie', 'vyuctovanie', 'benefic',
			'we are organizing', 'we organize', 'we would like to organize', 'our event', 'your platform', 'your fees', 'what are the fees', 'commission', 'integrate', 'integration', 'payout', 'cancel the event',
		],
	},
	{ target: 'flow:refund', stems: ['storno', 'stornovat', 'vratit vstupenku', 'vratit vstupenky', 'vratenie penazi', 'vratit peniaze', 'zrusit objednavku', 'refund', 'cancel my ticket', 'cancel the order', 'da sa vratit', 'vstupenka vratit', 'vstupenku vratit', 'listok vratit', 'mozem vratit', 'vratenie vstupenky', 'return the ticket', 'return my ticket', 'get a refund', 'money back'] },
	{ target: 'flow:invoice-change', stems: ['faktur', 'invoice', 'ico', 'ic dph', 'na firmu'], unless: ['nemam dic', 'nemame dic'] },
	{ target: 'flow:promo-code', stems: ['zlavovy kod', 'zlavovy kupon', 'promo kod', 'promo code', 'discount code', 'voucher', 'kupon', 'kod na zlavu'] },
	{
		// event-content questions wearing purchase words (owner, 2026-10-19): buying at the door, a
		// date the organizer has not listed, opening hours - only the organizer can answer these,
		// so they must be caught before the availability branch below sees "kúpiť" or "rezervácia"
		target: 'flow:event-question',
		stems: [
			'na mieste', 'kupit osobne', 'zakupit osobne', 'v pokladni', 'pri vstupe', 'na miste', 'koupit osobne', 'at the door', 'at the venue', 'on site', 'on the spot', 'box office',
			'rezervaciu na', 'rezervovat na', 'rezervaci na', 'termin na', 'book for', 'reserve for', 'reservation for', 'booking for',
			'hocikedy', 'kedykolvek', 'kdykoliv', 'otvaracie', 'oteviraci', 'kedy je otvoren', 'kdy je otevren', 'opening hours', 'anytime', 'any time',
			// cash on arrival: how Inviton is paid + whether the organizer takes cash on site (owner 2026-10-23: never claim it is impossible)
			'v hotovosti', 'hotovostou', 'hotovost', 'in cash', 'pay cash', 'cash on arrival', 'cash at the',
			// "can I buy as an individual, not a company" is about who the event is for, even with "buy" in it (week of 2026-10-23)
			'jednotlivec', 'nie som firma', 'sukromna osoba', 'fyzicka osoba', 'ako sukromna', 'as an individual', 'not a company', 'private person', 'private individual',
		],
	},
	{
		target: 'flow:availability',
		stems: ['da sa kupit', 'da sa este kupit', 'este kupit', 'mozem kupit', 'chcem kupit', 'kupit vstupenk', 'kupit listky', 'su volne', 'volne miesta', 'volny termin', 'volne vstupenky', 'vypredan', 'je este', 'su este', 'dostupn', 'rezervovat', 'rezervacia', 'buy a ticket', 'buy tickets', 'still available', 'sold out', 'any places', 'free places', 'book a', 'reserve a',
			// "why are the tickets no longer on sale" (week of 2026-10-23)
			'v predaji', 'nie su v predaji', 'uz nie su vstupenky', 'uz sa neda kupit', 'neda sa kupit', 'nie je mozne kupit', 'nejde kupit', 'no longer on sale', 'not on sale', 'sale ended', 'cannot buy', 'cant buy'],
	},
	{
		target: 'flow:event-question',
		stems: [
			'program', 'vystupov', 'vystupuj', 'kedy zacin', 'kedy vystup', 'co zahrna', 'v cene', 'obed', 'vekov', 'od kolko rokov', 'deti', 'online prenos', 'zaznam', 'parkovan', 'dress code', 'lineup', 'schedule', 'what time', 'included in the price',
			// children, age, prices, recordings (owner, 2026-10-19)
			'dieta', 'dietat', 'detsk', 'rocne', 'rocny', 'rocna', 'rokov', 'cena', 'cenu', 'ceny', 'cennik', 'kolko stoji', 'kolko stoja', 'zlava pre', 'prednas',
			'dite', 'detem', 'rocni', 'kolik stoji', 'cena pro', 'prednask',
			'children', 'child', 'kids', 'toddler', 'years old', 'how old', 'age limit', 'how much', 'price', 'prices', 'recording', 'recordings', 'lecture',
			// a booking for a weekday or for N people, differences between events, individuals vs companies (week of 2026-10-23)
			'na sobotu', 'na nedelu', 'na piatok', 'na stvrtok', 'na stredu', 'na utorok', 'na pondelok', 'pre dvoch', 'pre styroch', 'pre troch', 'pre 2', 'pre 3', 'pre 4', 'na nedeli', 'na patek', 'na ctvrtek', 'pro dva', 'pro ctyri',
			'for saturday', 'for sunday', 'for friday', 'for sat ', 'for sun ', 'for fri ', 'for two', 'for 2 people', 'for 3 people', 'for 4 people', 'for 2 ', 'for 4 ',
			'aky je rozdiel', 'rozdiel medzi', 'jaky je rozdil', 'rozdil mezi', 'what is the difference', 'difference between',
			'jednotlivec', 'nie som firma', 'sukromna osoba', 'fyzicka osoba', 'ako sukromna', 'as an individual', 'not a company', 'private person', 'private individual',
			// who is it for / what do I need / how does it go (owner, 2026-10-21)
			'pre koho', 'je to pre', 'je to aj pre', 'mozem prist', 'mozem sa zucastnit', 'moze prist', 'moze sa zucastnit', 'vhodne pre', 'zaciatocn', 'musim mat', 'treba mat', 'potrebujem mat', 'co si mam priniest', 'co si priniest', 'co si vziat', 'ako to prebieha', 'ako prebieha', 'prvykrat', 'prvy raz', 'bez skusenost', 'nemam vlastn', 'nemam este', 'nemam firmu',
			'pro koho', 'je to pro', 'mohu prijit', 'muzu prijit', 'muze prijit', 'vhodne pro', 'zacatecn', 'musim mit', 'potrebuji mit', 'co si vzit', 'co si prinest', 'jak to probiha', 'poprve', 'bez zkusenost', 'nemam vlastni',
			'open to', 'who can', 'can anyone', 'can i come', 'can i attend', 'can i join', 'suitable for', 'beginner', 'beginners', 'do i need', 'do i have to', 'have to have', 'need to have', 'need to bring', 'what to wear', 'is it for', 'first time', 'newcomer', 'what is it like', 'whats it like', 'what its like', 'own business', 'without experience', 'no experience',
		],
	},
];

/**
 * A stem matches from the start of a word: "ico" must not fire inside "Domenico", nor "obed"
 * inside "pobedu". Longer stems ("vypredan", "faktur") keep matching any ending of the word.
 */
const hasStem = (normalized: string, stem: string) => (' ' + normalized).includes(' ' + stem);

/** First matching route for a free-text question, or null when nothing is certain enough. */
export const routeFreeText = (text: unknown): string | null => {
	const t = normalizeName(text);
	if (t.length < 4) {
		return null;
	}
	for (const route of FREE_TEXT_ROUTES) {
		if (route.unless?.some((stem) => hasStem(t, stem))) {
			continue;
		}
		if (route.stems.some((stem) => hasStem(t, stem))) {
			return route.target;
		}
	}
	return null;
};

const other: SupportFlow = {
	id: 'other',
	intent: 'other',
	titleKey: 'flow.other',
	icon: '❓',
	priority: 1000,
	enabled: true,
	audience: 'both',
	entryStep: 'question',
	steps: [
		{
			id: 'question',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 3000,
			askMoreIfShort: 15,
			textKey: 'oth.askQuestion',
			placeholderKey: 'oth.placeholder',
			// First: can anyone answer this without a human? The router either sends the customer
			// to the branch that can (availability, order status, ticket, refund, …) or to one of
			// the one-sentence answers below. Only what it is unsure about keeps the old path:
			// on an event page confirm the page event, elsewhere ask for the name when the words
			// say the question is about one.
			next: (ctx) =>
				routeFreeText(ctx.answers.description) ??
				(hasEventContext(ctx) || looksLikeEventQuestion(ctx.answers.description) ? 'event' : 'contactEmail'),
		},
		...eventSteps('contactEmail', 'event.askNameOptional', { optional: true }),
		// --- answers support repeated in almost every week of the first five (analysis 2026-10-14).
		// Each of them asks whether it helped and hands over to support when it did not.
		...selfServiceSteps('kbPromoWhere', { textKey: 'kb.promoWhere' }, 'sit.kb.notEnough', 'contactEmail'),
		...selfServiceSteps('kbWalletPayment', { textKeys: ['kb.walletPayment', 'kb.walletPaymentSteps'] }, 'sit.kb.notEnough', 'contactEmail'),
		...selfServiceSteps('kbChangePayment', { textKey: 'kb.changePayment' }, 'sit.kb.notEnough', 'contactEmail'),
		...selfServiceSteps('kbBankTransfer', { textKey: 'kb.bankTransfer' }, 'sit.kb.notEnough', 'contactEmail'),
		...selfServiceSteps('kbBlueSeats', { textKey: 'kb.blueSeats' }, 'sit.kb.notEnough', 'contactEmail'),
		...selfServiceSteps('kbNoRegistration', { textKey: 'kb.noRegistration' }, 'sit.kb.notEnough', 'contactEmail'),
		...selfServiceSteps('kbDicOptional', { textKey: 'kb.dicOptional' }, 'sit.kb.notEnough', 'contactEmail'),
		...selfServiceSteps('kbNoPrint', { textKey: 'kb.noPrint' }, 'sit.kb.notEnough', 'contactEmail'),
		...selfServiceSteps('kbGiftVoucher', { textKey: 'kb.giftVoucher' }, 'sit.kb.notEnough', 'contactEmail'),
		...selfServiceSteps('kbAttendeeData', { textKey: 'kb.attendeeData' }, 'sit.kb.notEnough', 'contactEmail'),
		...selfServiceSteps('kbUseTicket', { textKey: 'kb.useTicket' }, 'sit.kb.notEnough', 'contactEmail'),
		resolvedStep,
		{ id: 'contactEmail', type: 'email', field: 'contactEmail', textKey: 'contact.askEmail', next: 'confirm' },
		...escalationTail('other', ['description', 'eventName', 'contactEmail'], false).filter(
			(s) => s.id !== 'contactEmail' && s.id !== 'contactEmailAsk',
		),
	],
};

export const supportFlows: SupportFlow[] = [
	availability,
	ticketNotReceived,
	orderStatus,
	paymentProblem,
	invoiceChange,
	changeData,
	eventQuestion,
	promoCode,
	refund,
	organizer,
	other,
];
